import { z } from "zod";

const AudioRecordingSchema = z.object({
  id: z.string(),
  url: z.string(),
  transcript: z.string().optional(),
  file: z.any(),
  blob: z.any(),
  duration: z.number(),
});

export const formSchema = z.object({
  project_id: z.string(),
  writing_about: z.string(),
  target_length: z.string(),
  attached_links: z.array(z.string().url()).optional(),
  attached_context_files: z
    .object({
      uploaded: z.array(z.any()).optional(),
      googleDrive: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
            file: z.any(),
          })
        )
        .optional(),
    })
    .optional(),
  attached_audios_transcripts: z.array(AudioRecordingSchema).optional(),
  notes: z.string().optional(),
  autofactcheck: z.boolean().optional(),
});

export type FormValues = z.infer<typeof formSchema>;

export const assistantFormSchema = z.object({
  user_id: z.string(),
  project_id: z.string(),
  chunk: z.string(),
  project_state: z.string(),
});

export type AssiantFormValues = z.infer<typeof assistantFormSchema>;

export interface AudioRecording {
  id: string;
  blob: Blob;
  url: string;
  duration: number;
}

export type CreateProjectResponse = {
  message: string;
  project_id: string;
};

export type assistantTypes =
  | "explain"
  | "summarize"
  | "rewrite"
  | "fact-check"
  | "counter-argument"
  | "references"
  | "dictionary";

export type Project = {
  id: string;
  user_id: string;
  name: string;
  base_content: string;
  base_content_updated_at: Date;
  current_content: string;
  current_content_updated_at: Date;
  context_information: Record<string, string>;
  target_length: string;
  writing_about: string;
  created_at: string;
  generation_is_save_for_later: boolean;
  updated_at: string;
};

export type ProjectsWithPagination = {
  projects: Pick<
    Project,
    | "id"
    | "name"
    | "created_at"
    | "generation_is_save_for_later"
    | "updated_at"
    | "current_content_updated_at"
  >[];
  total: number;
  page: number;
  total_pages: 1;
  page_size: 10;
};

export type TextPosition = {
  from: number;
  to: number;
  startCoords: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  endCoords: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

export type Product = {
  id: string;
  name: string;
  description: string;
  features: string[];
  prices: {
    monthly: {
      amount: number;
      lookup_key: string;
      currency: string;
    };
    yearly: {
      lookup_key: string;
      amount: number;
      currency: string;
    };
  };
};

export type UserProfile = {
  user_id: string;
  is_premium: boolean;
  is_personalizing: boolean;
  is_suggesting_topics: boolean;
};

export type CreateChatSessionPayload = {
  user_id: string;
  project_id: string;
  chat_type: "inline_chat" | "sidebar_chat";
  focus_chunk: string;
};

export type ChatSessionPayload = {
  project_id: string;
  chat_session_id: string;
  new_message: string;
};

export interface AudioRecording {
  id: string;
  url: string;
  transcript?: string;
  file: File;
  blob: Blob;
  duration: number;
}

export interface FetchProjectsResponse {
  projects: Pick<
    Project,
    | "id"
    | "name"
    | "created_at"
    | "generation_is_save_for_later"
    | "updated_at"
    | "current_content_updated_at"
  >[];
  nextCursor: number | null;
}

export type FetchProjectsResponseSupa = {
  projects: Pick<
    Project,
    | "id"
    | "name"
    | "created_at"
    | "updated_at"
    | "current_content_updated_at"
    | "target_length"
    | "base_content"
  >[];
  nextCursor: number | null;
};

export type SuggestedTopic = {
  topic: string;
  blueprint: string;
  open_source_links: Map<string, boolean>;
};

export type PersonalizationStatus =
  | "idle"
  | "in_progress"
  | "completed"
  | "long_running"
  | "taking_too_long"
  | "error"
  | "pending_notification";

export type SystemMessage =
  | "data_processing"
  | "thinking"
  | "auto_checking"
  | "reflecting"
  | "writing";

export type FactCheckCorrection = {
  original_chunk: string;
  rewritten_chunk: string;
};

export type DateCategory =
  | "Today"
  | "Yesterday"
  | "2 days ago"
  | "Last week"
  | "Reminder";

export type GroupedProjects = {
  [key in DateCategory]?: Pick<Project, "id" | "name" | "created_at">[];
};

export type Visibility = "PUBLIC" | "PRIVATE";

export type SharedProject = {
  visibility: Visibility;
  id: string;
  project_id: string;
  content: string | null;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;
};

export type ResponseErrorServerApi = {
  response?: {
    data?: {
      error?: null | string | Error;
      message: string;
      success: boolean;
    };
  };
};

export type SharedProjectViewer = {
  content: string;
  visibility: Visibility;
  updated_at: Date;
  project: {
    name: string;
  };
};

export type UsageStats = {
  ai_drafting: {
    current: number;
    max: number;
  };
  voice_transcription: {
    current: number;
    max: number;
  };
  ai_chat: {
    current: number;
    max: number;
  };
  personalization_document: {
    current: number;
    max: number;
  };
};

export type ChatSession = {
  project_id: string;
  id: string;
  user_id: string;
  terminated: boolean;
  name: string;
  created_at?: Date;
  updated_at?: Date;
};

export type ChatMessage = {
  message: string;
  sender: "user" | "inkriv";
  id: string;
  session_id: string;
  created_at?: Date;
  updated_at?: Date;
};

export type ApiErrorType = "FREEMIUM_LIMIT";

export class ApiError extends Error {
  detail: string;
  type?: ApiErrorType;

  constructor(detail: string, type?: ApiErrorType) {
    super(detail);
    this.name = "ApiError";
    this.detail = detail;
    if (type) this.type = type;

    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export type AuthResponse = {
  authTokens: {
    token: string;
    refreshToken: string | undefined;
  };
  user: User;
};

export type User = {
  email: string;
  name: string;
  id: string;
  is_email_verified: boolean;
  user_profile: Partial<UserProfile>;
  user_type: "43b7f3" | "91ac9e";
};

export type SingleProjectResponse = {
  name: string;
  writing_about: string;
  generation_is_save_for_later: boolean;
  created_at: Date;
  updated_at: Date;
  context_information: string[];
  current_content_updated_at: string;
  current_content: string;
  target_length: string;
};

export type SignelUserResponse = {
  email: string;
  name: string;
  id: string;
  is_email_verified: boolean;
  user_type: "43b7f3" | "91ac9e";
  user_profile: Partial<{
    user_id: string;
    is_premium: boolean;
    is_personalizing: boolean;
    is_suggesting_topics: boolean;
    project: {
      id: string;
      name: string;
      created_at: Date;
      updated_at: Date;
    }[];
  }>;
};

export class AppError extends Error {
  public error_message: string;
  public error_type: "FREEMIUM_LIMIT" | null;
  public original_error?: Error;

  constructor(
    error_message: string,
    error_type: "FREEMIUM_LIMIT" | null,
    message = "Something went wrong",
    error?: Error
  ) {
    super(message);
    this.error_message = error_message;
    this.error_type = error_type;
    this.original_error = error;

    Object.setPrototypeOf(this, AppError.prototype);
  }
}

export type UpdateUserInput = {
  name?: string;
  email?: string;
  password?: string;
  previousPassword?: string;
};

export type DashboardCountMetrics = {
  users_count: number;
  banned_users_count: number;
  premium_users_count: number;
  freemium_users_count: number;

  projects_count: number;
  shared_projects_count: number;
  new_signups_today_count: number;
  personalization_count: number;

  usage: {
    chat_token_usage: number;
    drafted_word_count: number;
    project_context_count: number;
    transcription_duration_sec: number;
  };
};

type DailyUsageChartData = {
  date: string;
  chat_token_usage: number;
  drafted_word_count: number;
  project_context_count: number;
  transcription_duration_sec: number;
};

type PersonalizationChartData = {
  date: string;
  total_personalization_duration_sec: number;
  document_count: number;
  documents_cumulative_size: number;
  initial_personalizations: number;
};

export type DashboardChartsData = {
  daily_usage: DailyUsageChartData[];
  personalization: PersonalizationChartData[];
};
